import { useTranslation } from 'react-i18next';
import { IssuePriority, type IIssue } from '@d-orca/shared/trusstor';
import { useUpdateIssue } from '@components/Pages/IssuesPage/hooks';
import {
  SafetyShieldFilledIcon,
  SafetyShieldIcon,
  StarFilledIcon,
  StarOutlineIcon,
} from '@components/Pages/IssuesPage/Board/icons';
import styles from './cardList.module.css';

interface IIssueMarksProps {
  issue: IIssue;
  /** The viewer holds `issues.manage` — toggles instead of read-only marks. */
  canManage: boolean;
  siteId: string | null;
}

/**
 * An issue card's critical star and safety shield. A manager toggles them in
 * place (safety needs a location to sit on); anyone else sees only the marks
 * that are set. Shared by the Activities & Issues card and the Monitor's.
 */
export function IssueMarks({ issue, canManage, siteId }: IIssueMarksProps) {
  const { t } = useTranslation();
  const update = useUpdateIssue(siteId);
  const isCritical = issue.priority === IssuePriority.CRITICAL;
  const isSafety = issue.isSafety === true;
  // Safety needs a location to sit on — to set it, never to clear it.
  const safetyDisabled = !isSafety && (!issue.locations || issue.locations.length === 0);

  if (!canManage) {
    if (!isCritical && !isSafety) return null;
    return (
      <span className={styles.titleActions}>
        {isCritical && (
          <span
            role="img"
            className={`${styles.stateIcon} ${styles.iconBtnFilled}`}
            aria-label={t('activities.filter.critical')}
            title={t('activities.filter.critical')}
          >
            <StarFilledIcon size={18} />
          </span>
        )}
        {isSafety && (
          <span
            role="img"
            className={`${styles.stateIcon} ${styles.iconBtnSafety}`}
            aria-label={t('activities.filter.safety')}
            title={t('activities.filter.safety')}
          >
            <SafetyShieldFilledIcon size={18} gradient />
          </span>
        )}
      </span>
    );
  }

  let safetyLabel = isSafety ? t('issues.hub.removeSafety') : t('issues.hub.markSafety');
  if (safetyDisabled) safetyLabel = t('issues.hub.safetyNeedsLocation');
  const criticalLabel = isCritical ? t('issues.hub.removeCritical') : t('issues.hub.markCritical');
  return (
    <span className={styles.titleActions}>
      <button
        type="button"
        className={`${styles.iconBtn} ${isCritical ? styles.iconBtnFilled : ''}`}
        onClick={(e) => {
          e.stopPropagation();
          update.mutate({
            issueId: issue._id,
            patch: { priority: isCritical ? IssuePriority.REGULAR : IssuePriority.CRITICAL },
          });
        }}
        onKeyDown={(e) => e.stopPropagation()}
        aria-label={criticalLabel}
        title={criticalLabel}
        aria-pressed={isCritical}
      >
        {isCritical ? <StarFilledIcon size={18} /> : <StarOutlineIcon size={18} />}
      </button>
      <button
        type="button"
        className={`${styles.iconBtn} ${isSafety ? styles.iconBtnSafety : ''}`}
        onClick={(e) => {
          e.stopPropagation();
          update.mutate({ issueId: issue._id, patch: { isSafety: !isSafety } });
        }}
        onKeyDown={(e) => e.stopPropagation()}
        disabled={safetyDisabled}
        aria-label={safetyLabel}
        title={safetyLabel}
        aria-pressed={isSafety}
      >
        {isSafety ? <SafetyShieldFilledIcon size={18} gradient /> : <SafetyShieldIcon size={18} />}
      </button>
    </span>
  );
}
